import React from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';

import {
  GetResourceValue,
  GetTasksByIds,
  GetTasksByZoneId,
  UpdateGameDocState,
  UpdateTaskAsync
} from '../../../utils/game-document';
import PopupMenu from '../../popupMenu';
import { MapConfigStore } from '../../../pages/game-designer';
import { TaskEntity } from '../../../types/game-document/entities';
import { GameDocumentContext } from '../../../contexts/game-document';
import { MenuList } from '../../../features/game-document/maps/map-images-window';

const taskMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
  { classIcon: 'visibility', textMenu: 'Preview', textClass: '' },
  { classIcon: 'content_copy', textMenu: 'Copy', textClass: '' },
  { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
];

export const Tasks: React.FC<{
  showAll: boolean;
  showTaskTitle: boolean;
  searchContentText: string;
  handleTaskMenuSelected: (id: string, menu: MenuList) => void;
}> = ({
  showAll,
  searchContentText,
  showTaskTitle,
  handleTaskMenuSelected
}) => {
  const { zoneId, gameId } = useParams();

  const [mapConfig, setMapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = React.useContext(GameDocumentContext);
  const { gameDocument } = state;

  const tasks = React.useMemo(() => {
    if (zoneId) {
      return GetTasksByZoneId(zoneId, gameDocument) ?? [];
    } else {
      const taskIds = gameDocument?.rules?.worldMap?.tasks?.map(
        (task) => task.taskAssId
      );

      if (taskIds) return GetTasksByIds(gameDocument, taskIds) ?? [];
    }

    return [];
  }, [zoneId, gameDocument]);

  const filteredTasks = React.useMemo(() => {
    return tasks.filter((task) => {
      const taskTitle = GetResourceValue(gameDocument!, task.titleResId ?? '');
      return taskTitle.toLowerCase().includes(searchContentText.toLowerCase());
    });
  }, [tasks, gameDocument, searchContentText]);

  const showTask = async (task: TaskEntity, isVisible: boolean) => {
    if (gameDocument) {
      task.isVisible = isVisible;
      const result = await UpdateTaskAsync(gameDocument, task.id, task);
      setState((state) => UpdateGameDocState(state, result));
    }
  };

  React.useEffect(() => {
    tasks.forEach((task) => {
      const taskContainer = document.getElementById(`taskContainer-${task.id}`);
      const detailTaskContainer = document.getElementById(
        `detailTaskContainer-${task.id}`
      );
      if (taskContainer && detailTaskContainer) {
        if (showAll || task.isVisible) {
          taskContainer.classList.remove('d-none');
        } else {
          taskContainer.classList.add('d-none');
        }

        const taskName = document.getElementById(`taskName-${task.id}`);
        if (showTaskTitle) {
          taskContainer.classList.add('expanded');
          detailTaskContainer.classList.remove('hidden');
          if (taskName) taskName.classList.remove('d-none');
        } else {
          taskContainer.classList.remove('expanded');
          detailTaskContainer.classList.add('hidden');
        }
      }
    });
  }, [tasks, showAll, showTaskTitle]);

  if (
    filteredTasks.length === 0 ||
    (searchContentText && filteredTasks.length === 0)
  )
    return null;

  return (
    <PanelBar
      onSelect={() => {
        setMapConfig((prev) => ({
          ...prev,
          mapContentTasks: !prev.mapContentTasks
        }));
      }}>
      <PanelBarItem
        id={'task-panel-bar'}
        expanded={mapConfig.mapContentTasks}
        className={'panel-bar-custom'}
        title={
          <div className={'d-flex gap-2 align-items-center'}>
            <span
              className="material-symbols-outlined"
              style={{
                color: '#4698CB'
              }}>
              flag_check
            </span>
            <span>Tasks</span>
          </div>
        }>
        {filteredTasks.map((task, index) => (
          <div
            key={task.id}
            className={
              'd-flex w-100 justify-content-between align-items-center mt-1 gap-2'
            }>
            <div
              id={`taskList-${task.id}`}
              style={{
                backgroundColor: '#4698CB',
                borderColor: '#e5633d',
                borderWidth: '3px'
              }}
              className={`card w-100 border-0`}>
              <div
                className={
                  'card-body p-2 d-flex w-100 justify-content-between align-items-center'
                }>
                <div
                  className={'d-flex gap-2 text-light align-items-center'}
                  style={{
                    position: 'relative',
                    minWidth: 0,
                    flex: 1
                  }}>
                  <span className={'material-symbols-outlined flex-shrink-0'}>
                    flag_check
                  </span>
                  {task.enableQRCode && (
                    <span
                      className={'material-symbols-outlined flex-shrink-0'}
                      style={{ opacity: 0.5 }}>
                      qr_code_scanner
                    </span>
                  )}
                  <span
                    title={GetResourceValue(
                      gameDocument!,
                      task.titleResId ?? ''
                    )}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0
                    }}>
                    {GetResourceValue(
                      gameDocument!,
                      task.titleResId ?? ''
                    )}{' '}
                  </span>
                </div>
                <div className={'flex-shrink-0'}>
                  <PopupMenu
                    id={index}
                    buttonColor="white"
                    menus={
                      task.taskContentId
                        ? taskMenu
                        : taskMenu.filter((e) => e.textMenu !== 'Preview')
                    }
                    onMenuSelected={(_, mn) =>
                      handleTaskMenuSelected(task.id, mn)
                    }
                    iconClass={'k-icon k-i-more-vertical h5 mb-0 text-light'}
                  />
                </div>
              </div>
            </div>

            <span
              onClick={() => {
                showTask(task, !task.isVisible);

                const taskContainer = document.getElementById(
                  `taskContainer-${task.id}`
                );
                if (taskContainer) {
                  if (task.isVisible) {
                    taskContainer.classList.remove('d-none');
                  } else {
                    taskContainer.classList.add('d-none');
                  }
                }
              }}
              className={'material-symbols-outlined'}>
              <span
                className={`${task.isVisible ? 'text-primary' : 'text-danger'} material-symbols-outlined cursor-pointer text-decoration-none`}>
                {task.isVisible ? 'visibility' : 'visibility_off'}
              </span>
            </span>
          </div>
        ))}
      </PanelBarItem>
    </PanelBar>
  );
};
